/**
 * Preferências de notificação por EVENTO, ESCOPO e CANAL (spec 037, PR B) — a
 * tabela de Minha conta › Notificações no padrão do TiFlux: duas colunas,
 * **Notificações** (o sino) e **E-mails**, e cada evento em duas linhas,
 * "(geral)" — todo ticket que a pessoa enxerga — e "nos meus tickets" — os em
 * que ela é a responsável.
 *
 * Tudo continua no MESMO jsonb `users.notification_prefs` (catchall de
 * booleanos), com uma chave por célula:
 *
 *   sino   · geral  <kind>               ← as chaves de sempre (ausente = ligada)
 *   sino   · meus   mine_<kind>          ← ausente = igual ao geral
 *   e-mail · geral  email_<kind>         ← ausente = DESLIGADA
 *   e-mail · meus   email_mine_<kind>    ← ausente = DESLIGADA
 *
 * **A migração é por interpretação, não por UPDATE.** Até a pessoa mexer na
 * coluna E-mails, o e-mail segue a regra de antes, calculada aqui
 * (`legacyEmailOn`): com `emailEnabled === true`, e-mail nos tickets em que ela
 * é a responsável, para comunicação pública, estágio, status (e encerramento)
 * e responsável, cada um enquanto o sino daquele tipo estiver ligado. Ao mexer
 * na coluna, o SPA grava o conjunto inteiro, explícito (`materializeEmailPrefs`),
 * com `emailPerEvent: true` — dali em diante valem as células. Quem já recebia
 * continua recebendo exatamente o mesmo; quem não recebia, nada muda.
 *
 * "Geral" inclui "nos meus": a célula de meus fica marcada (e travada na tela)
 * quando a de geral está ligada. Um evento gera UM e-mail por pessoa, mesmo
 * com as duas ligadas.
 */
import type { NotificationPrefs } from './account';

/**
 * Os eventos da tabela. `internal_comment_added` separa a comunicação interna
 * da pública ("nova resposta na comunicação interna" do TiFlux); no sino, a
 * chave ausente segue a de `comment_added` — quem desligou "Nova comunicação"
 * antes continua sem a interna.
 */
export const NOTIFICATION_EVENT_KINDS = [
  'ticket_created',
  'comment_added',
  'internal_comment_added',
  'mention',
  'stage_changed',
  'status_changed',
  'responsible_changed',
  'appointment_added',
] as const;
export type NotificationEventKind = (typeof NOTIFICATION_EVENT_KINDS)[number];

/**
 * Os que podem ir por e-mail: os que passam pelo funil de eventos do ticket. O
 * apontamento não passa — a linha dele mostra "—" na coluna E-mails.
 */
export const EMAIL_EVENT_KINDS = [
  'ticket_created',
  'comment_added',
  'internal_comment_added',
  'mention',
  'stage_changed',
  'status_changed',
  'responsible_changed',
] as const satisfies readonly NotificationEventKind[];
export type EmailEventKind = (typeof EMAIL_EVENT_KINDS)[number];

export const isEmailEventKind = (kind: NotificationEventKind): kind is EmailEventKind =>
  (EMAIL_EVENT_KINDS as readonly string[]).includes(kind);

export type NotificationScope = 'all' | 'mine';

/**
 * Eventos de UMA linha só (spec 037, PR C): a menção é sempre "nos meus" — ela
 * só chega a quem foi mencionado —, então não há geral nem meus. A célula é a
 * do geral (`mention`, `email_mention`), e "meus" lê a mesma.
 */
export const SINGLE_SCOPE_KINDS: readonly NotificationEventKind[] = ['mention'];
export const isSingleScopeKind = (kind: NotificationEventKind): boolean => SINGLE_SCOPE_KINDS.includes(kind);
export type NotificationChannel = 'bell' | 'email';

/** a chave do jsonb de cada célula */
export function notificationPrefKey(channel: NotificationChannel, scope: NotificationScope, kind: NotificationEventKind): string {
  if (channel === 'bell') return scope === 'all' ? kind : `mine_${kind}`;
  return scope === 'all' ? `email_${kind}` : `email_mine_${kind}`;
}

/** TODAS as chaves de célula — o PUT das preferências aceita estas */
export const NOTIFICATION_CELL_KEYS: readonly string[] = (['bell', 'email'] as const).flatMap((channel) =>
  (['all', 'mine'] as const).flatMap((scope) =>
    (channel === 'email' ? EMAIL_EVENT_KINDS : NOTIFICATION_EVENT_KINDS)
      .filter((kind) => scope === 'all' || !isSingleScopeKind(kind))
      .map((kind) => notificationPrefKey(channel, scope, kind))
  )
);

/** o sino, geral: ausente = ligada; a interna sem chave própria segue a pública */
function bellAll(prefs: NotificationPrefs, kind: NotificationEventKind): boolean {
  const own = prefs[kind];
  if (own !== undefined) return own;
  if (kind === 'internal_comment_added') return prefs['comment_added'] !== false;
  return true;
}

/**
 * O e-mail de ANTES (v5 §3.5), para quem ainda não mexeu na coluna E-mails: só
 * nos tickets em que a pessoa é a responsável, com `emailEnabled === true`, nos
 * eventos que o funil antigo mandava — e enquanto o sino daquele tipo estivesse
 * ligado (a chave do encerramento era a do status). A interna nunca ia.
 */
const LEGACY_EMAIL_KINDS: ReadonlySet<NotificationEventKind> = new Set([
  'comment_added',
  'stage_changed',
  'status_changed',
  'responsible_changed',
]);

export function legacyEmailOn(prefs: NotificationPrefs, scope: NotificationScope, kind: NotificationEventKind): boolean {
  if (scope !== 'mine') return false;
  if (prefs.emailEnabled !== true) return false;
  if (!LEGACY_EMAIL_KINDS.has(kind)) return false;
  return prefs[kind] !== false;
}

/** a pessoa já escolheu na coluna E-mails (as células valem; ausente = desligada) */
export const emailPerEvent = (prefs: NotificationPrefs): boolean => prefs.emailPerEvent === true;

/**
 * A célula está ligada? A regra única que o sino, o funil de e-mail e a tela
 * usam. "Meus" fica ligado quando o geral está (geral inclui meus).
 */
export function isNotificationOn(
  prefs: NotificationPrefs | undefined,
  channel: NotificationChannel,
  scope: NotificationScope,
  kind: NotificationEventKind
): boolean {
  const p = prefs ?? {};
  // a linha única: "meus" é a mesma célula do geral
  if (isSingleScopeKind(kind)) scope = 'all';
  if (channel === 'bell') {
    if (bellAll(p, kind)) return true;
    if (scope === 'all') return false;
    const mine = p[notificationPrefKey('bell', 'mine', kind)];
    // ausente = igual ao geral (que aqui está desligado): quem desligou um tipo
    // antes continua sem ele, até ligar "nos meus tickets" de propósito
    return mine === true;
  }
  if (!isEmailEventKind(kind)) return false;
  if (!emailPerEvent(p)) return legacyEmailOn(p, scope, kind);
  if (p[notificationPrefKey('email', 'all', kind)] === true) return true;
  return scope === 'mine' && p[notificationPrefKey('email', 'mine', kind)] === true;
}

/**
 * Fixa a coluna E-mails como a pessoa a vê agora — o que a regra antiga
 * calculava vira célula explícita — e liga `emailPerEvent`. O SPA chama antes
 * de gravar a primeira mudança na coluna.
 */
export function materializeEmailPrefs(prefs: NotificationPrefs): NotificationPrefs {
  if (emailPerEvent(prefs)) return prefs;
  const next: NotificationPrefs = { ...prefs, emailPerEvent: true };
  for (const kind of EMAIL_EVENT_KINDS) {
    next[notificationPrefKey('email', 'all', kind)] = isNotificationOn(prefs, 'email', 'all', kind);
    if (isSingleScopeKind(kind)) continue;
    next[notificationPrefKey('email', 'mine', kind)] = isNotificationOn(prefs, 'email', 'mine', kind);
  }
  return next;
}

/** Rótulos da tabela: o evento e a linha "nos meus tickets" de cada um. */
export const NOTIFICATION_KIND_LABELS: Record<NotificationEventKind, { all: string; mine: string }> = {
  ticket_created: { all: 'Abertura de novo ticket', mine: 'Recebido novo ticket (nos meus tickets)' },
  comment_added: { all: 'Nova comunicação nos tickets', mine: 'Nova comunicação nos meus tickets' },
  internal_comment_added: {
    all: 'Nova comunicação interna',
    mine: 'Nova comunicação interna nos meus tickets',
  },
  mention: { all: 'Menção na comunicação interna', mine: 'Menção na comunicação interna' },
  stage_changed: { all: 'Mudança de estágio', mine: 'Mudança de estágio nos meus tickets' },
  status_changed: { all: 'Mudança de status', mine: 'Mudança de status nos meus tickets' },
  responsible_changed: { all: 'Mudança de responsável', mine: 'Ticket atribuído a mim' },
  appointment_added: { all: 'Novo apontamento', mine: 'Novo apontamento nos meus tickets' },
};

/**
 * Endereço que não tem caixa: os logins de portal das associações têm e-mail
 * SINTÉTICO, `<nome>@<tenant>.erp.fatorh.ai` (scripts/provision-client-accounts,
 * spec 034). Nenhum e-mail de notificação vai para um deles — seria bounce
 * contra a reputação da conta SES inteira.
 */
export function isSyntheticEmail(email: string): boolean {
  const at = email.lastIndexOf('@');
  if (at < 0) return true;
  const domain = email.slice(at + 1).trim().toLowerCase().replace(/\.$/, '');
  return domain === 'erp.fatorh.ai' || domain.endsWith('.erp.fatorh.ai');
}
